import { isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { isBefore } from 'date-fns';

import { getMaxDate } from '@/api/dates/utils.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import {
  cancelFlexiForwardRfsMatchPending,
  checkFlexiForwardByTradeCaptureMatchFulfilled,
  flexiForwardApi,
  flexiForwardCancelRfsInitiate,
  flexiForwardExecuteTradeMatchPending,
  flexiForwardRequestRfsInitiate,
  flexiForwardRequestRfsMatchFulfilled,
} from '@/store/flexi-forward/api';
import { createFlexiForwardRfsRequestParams } from '@/store/flexi-forward/api/requestFlexiForwardRfs.ts';
import type { CheckFlexiForwardParams } from '@/store/flexi-forward/api/trade-capture/checkFlexiForward.model.ts';
import type { FlexiForwardTcResponse } from '@/store/flexi-forward/api/trade-capture/checkFlexiForward.ts';
import { getFlexiForwardRfsId } from '@/store/flexi-forward/helpers/rfs.ts';
import { getRequestFlexiForwardRfsMutationCacheKey } from '@/store/flexi-forward/selectors/rfs.ts';
import { selectFlexiForwardTile } from '@/store/flexi-forward/selectors/tile.ts';
import { flexiForwardSlice } from '@/store/flexi-forward/slice.ts';
import type { FxFlexiForward } from '@/store/flexi-forward/state.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppState } from '@/store/store.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerRfsFlexiForwardListeners = (startAppListening: StartAppListening) => {
  // When RFS request is made, we prepare a timeout to cancel it if no quote/heartbeat is received
  startAppListening({
    matcher: flexiForwardRequestRfsMatchFulfilled,
    effect: async (action, api) => {
      const { tileId, rfsId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }
        api.dispatch(flexiForwardCancelRfsInitiate({ rfsId, tileId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: flexiForwardApi.endpoints.executeFlexiForwardTrade.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  // When the clientId or productType of a flexi forward tile changes, or when the tile is removed,
  startAppListening({
    matcher: isAnyOf(
      flexiForwardSlice.actions.flexiForwardClientChanged.match,
      tilesSlice.actions.updateTileProductType.match,
      tilesSlice.actions.removeTile.match,
    ),
    effect: async (action, api) => {
      const state = api.getOriginalState();

      const tileId =
        flexiForwardSlice.actions.flexiForwardClientChanged.match(action) ||
        tilesSlice.actions.updateTileProductType.match(action)
          ? action.payload.tileId
          : tilesSlice.actions.removeTile.match(action)
            ? action.payload
            : undefined;

      if (isNotDefined(tileId)) {
        return;
      }

      const tile = selectFlexiForwardTile(state, tileId);

      if (isNotDefined(tile?.properties.rfs)) {
        return;
      }

      if (tile.properties.rfs.status === 'AWAITING' || tile.properties.rfs.status === 'PRICING') {
        api.dispatch(
          flexiForwardApi.endpoints.cancelFlexiForwardRfs.initiate({
            tileId,
            rfsId: tile?.properties.rfs.rfsId,
            removeQuote: true,
          }),
        );
      }
    },
  });

  // When an RFS is canceled, we clear the timeout
  startAppListening({
    matcher: isAnyOf(cancelFlexiForwardRfsMatchPending, flexiForwardExecuteTradeMatchPending),
    effect: async (action) => {
      const rfsId =
        cancelFlexiForwardRfsMatchPending(action) || flexiForwardExecuteTradeMatchPending(action)
          ? action.meta.arg.originalArgs.rfsId
          : undefined;

      if (isNotDefined(rfsId)) {
        return;
      }
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  // When a trade capture check is fulfilled, we may need to request a new RFS depending on the TC status
  startAppListening({
    matcher: checkFlexiForwardByTradeCaptureMatchFulfilled,

    effect: async (action, api) => {
      const { dispatch, getState } = api;
      const currentState = getState();

      const requestPayload: CheckFlexiForwardParams = action.meta.arg.originalArgs;
      const tradeCaptureResponse: FlexiForwardTcResponse = action.payload;

      const tileId = requestPayload.tileId;

      const tile = currentState.flexiForward[tileId];
      const clientId = selectTileClientId(currentState, tileId);

      if (isNotDefined(tile) || isDefined(tile.execution) || isNotDefined(clientId)) {
        return;
      }

      const { isReadyToPrice, isPriceObsolete, isProductObsolete } = getTradeCaptureStatus(
        tradeCaptureResponse.tradeCapture,
      );

      const currentRfsId = getFlexiForwardRfsId(tile.properties.rfs) ?? null;

      // ON A UN CURRENT RFS ID
      if (isDefined(currentRfsId)) {
        if (isReadyToPrice && !isPriceObsolete && !isProductObsolete) {
          // When TC replies ready to price but price/product aren't obsolete, we do nothing
          return;
        }

        if (isProductObsolete) {
          await dispatch(flexiForwardCancelRfsInitiate({ rfsId: currentRfsId, tileId }));
        } else if (isPriceObsolete && isReadyToPrice) {
          await dispatch(flexiForwardCancelRfsInitiate({ rfsId: currentRfsId, tileId }));

          const signalRConnectionId = getSignalRConnectionId(getState());
          const rfsId = getNewRfsId();
          const rfsRequestParams = createFlexiForwardRfsRequestParams(
            tile,
            tileId,
            rfsId,
            clientId,
            signalRConnectionId,
          );

          if (isDefined(rfsRequestParams)) {
            await dispatch(
              flexiForwardRequestRfsInitiate(rfsRequestParams, {
                fixedCacheKey: getRequestFlexiForwardRfsMutationCacheKey(tileId),
              }),
            );
          }
        }
      }

      // PAS DE CURRENT RFS ID
      const isObsolete = !isPriceObsolete || isProductObsolete;
      const currentUserCache = selectGetCurrentUser(currentState).data?.clients;

      const maxDate = await getMaxDate(clientId, currentUserCache, dispatch, tile.properties.currencyPair);
      dispatch(flexiForwardSlice.actions.setMaxDate({ tileId, maxDate }));
      const isValidExpiryDate = isValidDate(currentState, tileId, maxDate);

      if (isReadyToPrice && isObsolete && isValidExpiryDate) {
        const signalRConnectionId = getSignalRConnectionId(getState());

        const rfsId = getNewRfsId();
        const rfsRequestParams = createFlexiForwardRfsRequestParams(tile, tileId, rfsId, clientId, signalRConnectionId);

        if (isDefined(rfsRequestParams)) {
          await dispatch(
            flexiForwardRequestRfsInitiate(rfsRequestParams, {
              fixedCacheKey: getRequestFlexiForwardRfsMutationCacheKey(tileId),
            }),
          );
        }
      }
    },
  });
};

interface TcData {
  isReadyToPrice: boolean;
  isProductObsolete: boolean;
  isPriceObsolete: boolean;
}

function getTradeCaptureStatus(tradeCapture: FxFlexiForward['tradeCapture']): TcData {
  if (tradeCapture?.status !== 'CHECKED') {
    return {
      isReadyToPrice: false,
      isProductObsolete: false,
      isPriceObsolete: false,
    };
  }

  return {
    isReadyToPrice: tradeCapture?.isReadyToPrice,
    isProductObsolete: tradeCapture?.isProductObsolete,
    isPriceObsolete: tradeCapture?.isPriceObsolete,
  };
}

function isValidDate(state: AppState, flexiForwardId: string, maxDate: string | undefined): boolean {
  const expiryDate = state.flexiForward[flexiForwardId]?.properties?.expiryDate;

  if (isNotDefined(expiryDate) || isNotDefined(maxDate)) {
    return true;
  }

  return isBefore(expiryDate, maxDate);
}
